import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  Card,
  Form,
  Input,
  Select,
  Button,
  message,
  Spin,
  Switch
} from 'antd'
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons'
import styled from 'styled-components'
import { useAuth } from '../hooks/AuthContext'
import useGoBack from '../hooks/useGoBack'
import { useNavigate, useParams } from 'react-router-dom'
import api from '../utils/api'

const PageContainer = styled.div`
  padding: 24px;
  background: #f5f5f5;
  min-height: 100vh;
`

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
`

const FormCard = styled(Card)`
  max-width: 640px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
`

const RELATIONSHIP_OPTIONS = [
  { value: 'family', label: '家庭' },
  { value: 'company', label: '公司' },
  { value: 'friends', label: '朋友' },
  { value: 'other', label: '其他' }
]

const CustomerGroupFormPage = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { groupId } = useParams()
  const [form] = Form.useForm()
  const isEdit = Boolean(groupId)
  const goBack = useGoBack(
    isEdit ? `/customers/groups/${groupId}` : '/customers/groups'
  )
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [customers, setCustomers] = useState([])
  const [initialMemberOptions, setInitialMemberOptions] = useState([]) // 編輯時從群組成員帶入
  const [searching, setSearching] = useState(false)
  const searchTimeoutRef = useRef(null)

  const businessId = user?.businessId

  const fetchGroup = useCallback(async () => {
    if (!businessId || !groupId) return
    setLoading(true)
    try {
      const res = await api.get(
        `/api/business_B/${businessId}/customers/groups/${groupId}`
      )
      const g = res.data?.data ?? res.data
      if (!g) throw new Error('群組不存在')
      const members = g.members ?? []
      const memberIds = members.map((m) => m._id ?? m.id ?? m).filter(Boolean)
      const opts = members.map((m) => {
        const id = m._id ?? m.id
        const name = m.name ?? m.userInfo?.name ?? '未命名'
        const phone = m.phone ?? m.userInfo?.phone ?? ''
        return { value: id, label: `${name}${phone ? ` (${phone})` : ''}` }
      })
      setInitialMemberOptions(opts)
      form.setFieldsValue({
        name: g.name,
        description: g.description ?? '',
        relationship: g.relationship ?? 'family',
        primaryContactId: g.primaryContact?._id ?? g.primaryContact?.id ?? memberIds[0],
        notes: g.notes ?? '',
        isActive: g.isActive !== false,
        memberIds
      })
    } catch (err) {
      console.error('取得群組失敗:', err)
      message.error(err?.response?.data?.message || '取得群組失敗')
      navigate('/customers/groups')
    } finally {
      setLoading(false)
    }
  }, [businessId, groupId, form, navigate])

  useEffect(() => {
    if (isEdit) fetchGroup()
  }, [isEdit, fetchGroup])

  const searchCustomers = useCallback(
    async (keyword) => {
      if (!businessId) return
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current)
      if (!keyword || !keyword.trim()) {
        setCustomers([])
        return
      }
      searchTimeoutRef.current = setTimeout(async () => {
        setSearching(true)
        try {
          const res = await api.get(
            `/api/business_B/${businessId}/customers/search`,
            { params: { searchTerm: keyword.trim() } }
          )
          const list = res.data?.data ?? res.data ?? []
          setCustomers(Array.isArray(list) ? list : [])
        } catch (e) {
          console.error('搜尋顧客失敗:', e)
          setCustomers([])
        } finally {
          setSearching(false)
        }
      }, 300)
    },
    [businessId]
  )

  const handleSubmit = async () => {
    try {
      const v = await form.validateFields()
      if (!v.memberIds?.length) {
        message.warning('請至少選擇一位成員')
        return
      }
      setSaving(true)
      const payload = {
        name: v.name?.trim(),
        description: v.description?.trim() || undefined,
        memberIds: v.memberIds,
        relationship: v.relationship || 'family',
        primaryContactId: v.primaryContactId || v.memberIds[0],
        notes: v.notes?.trim() || undefined,
        isActive: v.isActive !== false
      }
      if (isEdit) {
        await api.put(
          `/api/business_B/${businessId}/customers/groups/${groupId}`,
          payload
        )
        message.success('群組已更新')
        navigate(`/customers/groups/${groupId}`)
      } else {
        await api.post(
          `/api/business_B/${businessId}/customers/groups`,
          payload
        )
        message.success('群組已建立')
        navigate('/customers/groups')
      }
    } catch (e) {
      if (e?.errorFields) return
      console.error('儲存群組失敗:', e)
      message.error(e?.response?.data?.message || '儲存失敗，請稍後再試')
    } finally {
      setSaving(false)
    }
  }

  const searchOptions = customers.map((c) => {
    const id = c._id ?? c.id
    const name = c.name ?? c.userInfo?.name ?? c.username ?? '未命名'
    const phone = c.phone ?? c.userInfo?.phone ?? ''
    return { value: id, label: `${name}${phone ? ` (${phone})` : ''}` }
  })
  const memberOptions = [...initialMemberOptions]
  searchOptions.forEach((o) => {
    if (!memberOptions.some((m) => m.value === o.value)) memberOptions.push(o)
  })

  return (
    <PageContainer>
      <PageHeader>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={goBack}
        >
          返回
        </Button>
        <span style={{ fontSize: 18, fontWeight: 600 }}>
          {isEdit ? '編輯群組' : '新增群組'}
        </span>
      </PageHeader>

      <Spin spinning={loading}>
        <FormCard>
          <Form
            form={form}
            layout='vertical'
            initialValues={{
              relationship: 'family',
              isActive: true,
              memberIds: []
            }}
            onFinish={handleSubmit}
          >
            <Form.Item
              name='name'
              label='群組名稱'
              rules={[{ required: true, message: '請輸入群組名稱' }]}
            >
              <Input placeholder='例如：高家' />
            </Form.Item>

            <Form.Item name='description' label='描述'>
              <Input.TextArea rows={2} placeholder='選填' />
            </Form.Item>

            <Form.Item
              name='memberIds'
              label='成員'
              rules={[{ required: true, message: '請至少選擇一位成員' }]}
            >
              <Select
                mode='multiple'
                placeholder='搜尋並選擇顧客作為成員'
                filterOption={false}
                onSearch={searchCustomers}
                loading={searching}
                options={memberOptions}
                notFoundContent={searching ? '搜尋中...' : '輸入關鍵字搜尋顧客'}
                showSearch
                optionFilterProp='label'
              />
            </Form.Item>

            <Form.Item name='primaryContactId' label='主要聯絡人'>
              <Select
                placeholder='選填，預設為第一位成員'
                allowClear
                options={memberOptions}
              />
            </Form.Item>

            <Form.Item name='relationship' label='關係類型'>
              <Select options={RELATIONSHIP_OPTIONS} />
            </Form.Item>

            <Form.Item name='notes' label='備註'>
              <Input.TextArea rows={2} placeholder='選填' />
            </Form.Item>

            {isEdit && (
              <Form.Item name='isActive' label='啟用' valuePropName='checked'>
                <Switch checkedChildren='啟用' unCheckedChildren='停用' />
              </Form.Item>
            )}

            <Form.Item>
              <Button type='primary' htmlType='submit' icon={<SaveOutlined />} loading={saving}>
                儲存
              </Button>
            </Form.Item>
          </Form>
        </FormCard>
      </Spin>
    </PageContainer>
  )
}

export default CustomerGroupFormPage
